import { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Polyline,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Thermometer, Wind, Cloud, X } from "lucide-react";

/* ---------- Itinerary ---------- */
export const ITINERARY = [
  { day: "01", name: "Newark, NJ", state: "NJ", coords: [40.7357, -74.1724], fact: "The journey begins where the Atlantic meets American legend — the starting point of a continent-wide search for the unseen.", site: "Road Departure" },
  { day: "02", name: "Cleveland", state: "OH", coords: [41.4993, -81.6944], fact: "Lake Erie's quiet powerhouse — Rock & Roll Hall of Fame country, Cuyahoga river bends, and the warm, working-class soul of Ohio's North Coast.", site: "Lake Erie Waterfront" },
  { day: "07", name: "Findley State Park", state: "OH", coords: [41.1373, -82.2127], fact: "Quiet woods cradling a shimmering lake — an unexpected pocket of calm between the steel towns and the prairies.", site: "Findley Lake" },
  { day: "08", name: "Wheeling", state: "IL", coords: [42.1392, -87.9289], fact: "A brief suburban waypoint on the outskirts of Chicago, bridging the Rust Belt to the heartland's wide-open rhythm.", site: "Wheeling Stopover" },
  { day: "09-10", name: "Chicago", state: "IL", coords: [41.8781, -87.6298], fact: "The Windy City — where deep-dish meets deep spirit. Blues echoes under the L tracks, and the lake horizon stretches like a landlocked ocean.", site: "Cloud Gate & Lakefront" },
  { day: "11", name: "Minneapolis", state: "MN", coords: [44.9778, -93.2650], fact: "City of Lakes and the mighty Mississippi headwaters — Prince's purple kingdom and a bridge between eastern grit and western sky.", site: "Chain of Lakes" },
  { day: "12", name: "Badlands NP", state: "SD", coords: [43.8554, -101.9777], fact: "Alien spires of layered sediment rise from the prairie — 75 million years of Earth's diary exposed in surreal, wind-carved buttes.", site: "Badlands National Park" },
  { day: "13", name: "Mount Rushmore", state: "SD", coords: [43.8791, -103.4591], fact: "Four presidential faces carved into granite — a monument to democracy's ambition set among the sacred Black Hills of the Lakota people.", site: "Mount Rushmore National Memorial" },
  { day: "13", name: "Needles", state: "SD", coords: [43.8210, -103.5066], fact: "Granite towers pierce the Black Hills sky — the Needles Highway is a spiritual corridor of stone cathedrals and narrow tunnels.", site: "Needles Highway" },
  { day: "14-15", name: "Yellowstone NP", state: "WY", coords: [44.4280, -110.5885], fact: "Earth's living laboratory — boiling mud pots, prismatic springs, and Old Faithful's clockwork eruptions over a supervolcano's beating heart.", site: "Yellowstone National Park" },
  { day: "16", name: "Butte", state: "MT", coords: [46.0038, -112.5347], fact: "The Richest Hill on Earth — a century of copper mining carved an open-pit scar now filling with toxic water, a monument to extraction's cost.", site: "Berkeley Pit Overlook" },
  { day: "17-18", name: "Seattle", state: "WA", coords: [47.6062, -122.3321], fact: "Emerald City — where tech titans coexist with totem poles, Pike Place fish-throwers, and the misty silhouette of Mount Rainier.", site: "Pike Place Market & Space Needle" },
  { day: "19", name: "San Francisco", state: "CA", coords: [37.7749, -122.4194], fact: "Golden Gate fog, cable-car clangs, and a counter-culture legacy that still hums through Haight-Ashbury and the Mission murals.", site: "Golden Gate Bridge" },
  { day: "21-22", name: "Yosemite NP", state: "CA", coords: [37.8651, -119.5383], fact: "Cathedral of granite — Half Dome, El Capitan, and waterfalls that plunge thousands of feet into a valley John Muir called 'a temple of nature.'", site: "Yosemite National Park" },
  { day: "23", name: "Sequoia NP", state: "CA", coords: [36.4864, -118.5658], fact: "Home of General Sherman — the largest living tree on Earth. Walking among these ancient giants is a humbling communion with deep time.", site: "Sequoia National Park" },
  { day: "24", name: "San Diego", state: "CA", coords: [32.7157, -117.1611], fact: "Sun-drenched Pacific shores — where California's mission trail began and surf culture blooms year-round under endless blue sky.", site: "Balboa Park & Pacific Beach" },
  { day: "24", name: "Tucson", state: "AZ", coords: [32.2226, -110.9747], fact: "Sonoran Desert's heart — saguaro sentinels, Tohono O'odham heritage, and sunsets that ignite the Santa Catalina mountains in copper and violet.", site: "Saguaro National Park" },
  { day: "25", name: "Las Vegas", state: "NV", coords: [36.1699, -115.1398], fact: "Neon oasis in the Mojave — a surreal monument to human desire rising from the desert floor, surrounded by red-rock wilderness.", site: "The Strip & Red Rock Canyon" },
  { day: "26", name: "Zion NP", state: "UT", coords: [37.2982, -113.0263], fact: "Crimson canyon walls soar 2,000 feet above the Virgin River — a sanctuary the Paiute called 'the heavenly city of God.'", site: "Zion National Park" },
  { day: "27", name: "Arches NP", state: "UT", coords: [38.7331, -109.5925], fact: "Over 2,000 natural stone arches — Delicate Arch at sunset is one of the most iconic silhouettes in the American West.", site: "Arches National Park" },
  { day: "28", name: "Monument Valley", state: "UT", coords: [36.9975, -110.0987], fact: "Sandstone buttes and mesas that define the cinematic West — sacred Navajo land where the Earth speaks in towering red monoliths.", site: "Monument Valley Navajo Tribal Park" },
  { day: "29", name: "Mesa Verde NP", state: "CO", coords: [37.2309, -108.4618], fact: "Ancestral Puebloan cliff dwellings tucked beneath sandstone overhangs — 700 years of human ingenuity preserved in stone.", site: "Mesa Verde National Park" },
  { day: "30", name: "Great Sand Dunes NP", state: "CO", coords: [37.7325, -105.5121], fact: "North America's tallest dunes — 750 feet of wind-sculpted sand rising against the snow-capped Sangre de Cristo range.", site: "Great Sand Dunes National Park" },
  { day: "31", name: "Hays", state: "KS", coords: [38.8792, -99.3268], fact: "High plains heartland — once a frontier outpost on the Smoky Hill Trail, now a quiet crossroads under the immense Kansas sky.", site: "Historic Fort Hays" },
  { day: "32", name: "Kansas City", state: "MO", coords: [39.0997, -94.5786], fact: "BBQ capital and jazz crossroads — where the Missouri and Kansas rivers merge and Charlie Parker's saxophone still echoes.", site: "18th & Vine Jazz District" },
  { day: "33", name: "Rolla", state: "MO", coords: [37.9485, -91.7715], fact: "A small Ozark college town on Route 66 — known for its Stonehenge replica and mining heritage.", site: "Rolla Route 66 Waypoint" },
  { day: "33", name: "Meramec State Park", state: "MO", coords: [38.2126, -91.0910], fact: "40+ caves riddle the bluffs above the Meramec River — one of Missouri's most cave-dense landscapes.", site: "Meramec Caverns" },
  { day: "34", name: "The Gateway Arch (St. Louis)", state: "MO", coords: [38.6247, -90.1848], fact: "630 feet of stainless steel soaring over the Mississippi — the Gateway to the West, a monument to America's restless expansion.", site: "Gateway Arch National Park" },
  { day: "35", name: "Bloomington — Mongolian Buddhist Cultural Center", state: "IN", coords: [39.1832, -86.5083], fact: "A living Mongolian Buddhist temple in the Indiana heartland — where ancient steppe spirituality meets American soil.", site: "Mongolian Buddhist Cultural Center" },
  { day: "36", name: "Virginia", state: "VA", coords: [37.5407, -77.4360], fact: "The Old Dominion — where colonial history layers over deep Appalachian roots and the Shenandoah Valley whispers of older worlds.", site: "Richmond — Virginia Spiritual Stop" },
  { day: "37", name: "Washington, D.C. — National Mall", state: "DC", coords: [38.8895, -77.0353], fact: "The nation's ceremonial heart — monuments, memorials, and the weight of democracy lined along a two-mile corridor of American aspiration.", site: "National Mall" },
  { day: "38", name: "Nitsan Temple (Howell, NJ)", state: "NJ", coords: [40.1789, -74.3126], fact: "A Tibetan Buddhist retreat center in the New Jersey Pine Barrens — meditation and mindfulness rooted in ancient Himalayan wisdom.", site: "Nitsan Temple" },
  { day: "38", name: "Liberty State Park", state: "NJ", coords: [40.7038, -74.0525], fact: "Sweeping views of the Statue of Liberty and Ellis Island — where millions first glimpsed the promise of America.", site: "Liberty State Park" },
  { day: "39", name: "Watkins Glen State Park", state: "NY", coords: [42.3782, -76.8712], fact: "A two-mile gorge of 200-foot cliffs and 19 waterfalls — water-carved cathedral of Devonian shale in the Finger Lakes.", site: "Watkins Glen Gorge" },
  { day: "40", name: "Niagara Falls", state: "NY", coords: [43.0962, -79.0377], fact: "Six million cubic feet of water per minute thundering over the brink — raw planetary power on the US-Canada border.", site: "Niagara Falls State Park" },
  { day: "40+", name: "JFK Airport, NY (End)", state: "NY", coords: [40.6413, -73.7781], fact: "The odyssey ends where the world connects — 36 days, 21 states, 11,000 miles of spirit, road, and discovery.", site: "Journey's End" },
];

/* ---------- Weather helper ---------- */
const WMO_ICONS = {
  0: "☀️", 1: "🌤", 2: "⛅", 3: "☁️",
  45: "🌫", 48: "🌫", 51: "🌦", 53: "🌦", 55: "🌧",
  61: "🌧", 63: "🌧", 65: "🌧‍", 71: "❄️", 73: "❄️", 75: "❄️",
  80: "🌦", 81: "🌧", 82: "⛈", 95: "⛈", 96: "⛈", 99: "⛈",
};

function useWeather(coords) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const [lat, lon] = coords;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`;
    fetch(url)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const c = j.current;
        setWeather({
          temp: Math.round(c.temperature_2m),
          code: c.weather_code,
          wind: Math.round(c.wind_speed_10m),
          humidity: c.relative_humidity_2m,
          icon: WMO_ICONS[c.weather_code] || "🌡",
        });
      })
      .catch(() => !cancelled && setError("Weather unavailable"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [coords?.[0], coords?.[1]]);
  return { weather, loading, error };
}

/* ---------- Map auto-fit ---------- */
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] });
  }, [bounds, map]);
  return null;
}

/* ---------- Stop Detail Card ---------- */
function StopCard({ stop, onClose, onPrev, onNext, hasPrev, hasNext }) {
  const { weather, loading } = useWeather(stop.coords);
  return (
    <div
      data-testid="map-stop-panel"
      style={{
        position: "absolute", top: 16, right: 16, zIndex: 1000,
        width: 340, background: "rgba(10,10,14,0.95)",
        border: "1px solid rgba(212,168,67,0.4)", borderRadius: 12,
        padding: 20, color: "#fff", fontFamily: "'Inter',sans-serif",
        backdropFilter: "blur(12px)", boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
      }}
    >
      <button
        onClick={onClose}
        data-testid="map-panel-close"
        style={{
          position: "absolute", top: 10, right: 10, background: "none",
          border: "none", color: "#d4a843", cursor: "pointer", padding: 4,
        }}
      ><X size={18} /></button>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <MapPin size={16} color="#d4a843" />
        <span style={{ color: "#d4a843", fontSize: 12, fontWeight: 600, letterSpacing: 1 }}>
          DAY {stop.day}
        </span>
      </div>

      <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700 }}>{stop.name}</h3>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "#aaa" }}>{stop.site}</p>
      <p style={{ margin: "0 0 16px", fontSize: 13, lineHeight: 1.6, color: "#ccc" }}>{stop.fact}</p>

      {weather && (
        <div style={{
          display: "flex", gap: 16, padding: "10px 12px",
          background: "rgba(212,168,67,0.08)", borderRadius: 8, fontSize: 12,
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Thermometer size={13} color="#d4a843" /> {weather.temp}°F
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Wind size={13} color="#d4a843" /> {weather.wind} mph
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Cloud size={13} color="#d4a843" /> {weather.humidity}%
          </span>
          <span>{weather.icon}</span>
        </div>
      )}
      {loading && <p style={{ fontSize: 12, color: "#888" }}>Loading weather…</p>}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <button disabled={!hasPrev} onClick={onPrev} style={{
          background: "none", border: "1px solid rgba(212,168,67,0.3)",
          color: hasPrev ? "#d4a843" : "#555", borderRadius: 6,
          padding: "6px 14px", cursor: hasPrev ? "pointer" : "default", fontSize: 12,
        }}>← Prev</button>
        <button disabled={!hasNext} onClick={onNext} style={{
          background: "none", border: "1px solid rgba(212,168,67,0.3)",
          color: hasNext ? "#d4a843" : "#555", borderRadius: 6,
          padding: "6px 14px", cursor: hasNext ? "pointer" : "default", fontSize: 12,
        }}>Next →</button>
      </div>
    </div>
  );
}

/* ========== MAIN COMPONENT ========== */
export default function RouteMap() {
  const [selected, setSelected] = useState(null);
  const bounds = useMemo(
    () => L.latLngBounds(ITINERARY.map((s) => s.coords)),
    []
  );

  const polyline = useMemo(() => ITINERARY.map((s) => s.coords), []);

  const idx = selected !== null ? selected : -1;

  return (
    <div
      data-testid="route-map"
      style={{ position: "relative", width: "100%", height: 600, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(212,168,67,0.3)" }}
    >
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        {/* FREE dark-themed tiles: standard OSM with CSS invert filter — no API key, no watermark */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          className="dark-tiles"
        />
        <FitBounds bounds={bounds} />

        {/* Route polyline */}
        <Polyline
          positions={polyline}
          pathOptions={{ color: "#d4a843", weight: 2, opacity: 0.6, dashArray: "8 6" }}
        />

        {/* Stop markers */}
        {ITINERARY.map((stop, i) => (
          <CircleMarker
            key={i}
            center={stop.coords}
            radius={selected === i ? 10 : 6}
            pathOptions={{
              color: selected === i ? "#fff" : "#d4a843",
              fillColor: selected === i ? "#d4a843" : "rgba(212,168,67,0.3)",
              fillOpacity: selected === i ? 1 : 0.7,
              weight: selected === i ? 3 : 1.5,
            }}
            eventHandlers={{ click: () => setSelected(i) }}
          >
            <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
              <span style={{ fontWeight: 600 }}>Day {stop.day}: {stop.name}</span>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      {/* Stop detail card */}
      {selected !== null && (
        <StopCard
          stop={ITINERARY[selected]}
          onClose={() => setSelected(null)}
          onPrev={() => setSelected(idx - 1)}
          onNext={() => setSelected(idx + 1)}
          hasPrev={idx > 0}
          hasNext={idx < ITINERARY.length - 1}
        />
      )}

      {/* CSS to darken OSM tiles for the dark theme */}
      <style>{`
        .dark-tiles {
          filter: invert(1) hue-rotate(180deg) brightness(0.7) contrast(1.3) saturate(0.3);
        }
        .leaflet-control-attribution {
          background: rgba(0,0,0,0.7) !important;
          color: #888 !important;
          font-size: 10px !important;
        }
        .leaflet-control-attribution a {
          color: #aaa !important;
        }
      `}</style>
    </div>
  );
}
